import React, { useState, useRef } from "react";
import "./contentpage.css";
import LandingSection from "./LandingSection";
import Question from "../components/Question";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage13() {
  const contentRef = useRef(null);
   const links = {
    prev: "/islamic-trade-page-12", 
    index: "/index",                 
    next: "/islamic-trade-page-14", 
  };
  const titleText = "Rulings on Guarantee (Dhaman) and Patronage (Kafalah)";
  const questions = [
    {
      text: "1. What does Dhaman (guarantee) primarily involve in Islamic finance?",
      options: [
        {
          value: "A",
          label: "A. Guaranteeing the physical presence of a person.",
        },
        {
          value: "B",
          label: "B. Guaranteeing another person’s emotional well-being.",
        },
        {
          value: "C",
          label:
            "C. Guaranteeing fulfillment of another’s financial obligation.",
        },
        {
          value: "D",
          label: "D. Guaranteeing business success.",
        },
      ],
      correct: "C",
    },
    {
      text: "2.  Which of the following is a valid form of Kafalah?",
      options: [
        {
          value: "A",
          label: "A. A person agrees to pay off another's debt in full.",
        },
        {
          value: "B",
          label:
            "B. A person guarantees that another will be present at a required time.",
        },
        {
          value: "C",
          label: "C. A person lends money without expecting it back.",
        },
        {
          value: "D",
          label:
            "D. A person agrees to invest in a business on behalf of a friend.",
        },
      ],
      correct: "B",
    },
    {
      text: "3. What happens to the responsibility of the Kafeel in Kafalah if the Makfool dies?",
      options: [
        { value: "A", label: "A. The Kafeel must pay the Makfool’s debts." },
        { value: "B", label: "B.  The Kafeel is no longer responsible." },
        {
          value: "C",
          label: "C. The Kafeel must attend court in place of the Makfool.",
        },
        {
          value: "D",
          label:
            "D. The Kafeel is required to pay compensation to the creditor.",
        },
      ],
      correct: "B",
    },
    {
      text: "4. Which of the following is not a condition for a valid Dhaman contract??",
      options: [
        { value: "A", label: "A. The guarantee must be offered willingly." },
        { value: "B", label: "B. It must involve mutual consent." },
        { value: "C", label: "C. The Makfool must give explicit permission." },
        {
          value: "D",
          label: "D. The obligation must be lawful under Shariah.",
        },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={13}
        timerMinutes={60}
        currentLevel={1}
      />
      <p className="content" ref={contentRef}>
        <b>The Concept and Foundations of Dhaman in Islamic Law</b>
        <br />
        <br />
        Welcome to <b>Day 21</b>! Today we will learn about Dhaman and Kafalah,
        two important concepts in Islamic finance that emphasize responsibility,
        trust, and social cooperation. Dhaman refers to the act of guaranteeing
        another person’s financial obligation or liability, while Kafalah
        involves taking responsibility for a person’s performance or appearance,
        such as ensuring someone fulfills a promise or attends a proceeding.
        Both principles reflect the Qur’anic and Prophetic encouragement to
        support one another in righteousness and to build a society based on
        mutual trust, compassion, and moral accountability.
        <br />
        The concept of Dhaman, or guarantee, occupies an essential position in
        Islamic commercial and financial jurisprudence. It is an instrument that
        safeguards transactions, ensures the fulfillment of debts, and fosters
        trust among members of society. Linguistically, the word Dhaman in
        Arabic refers to assurance, security, or taking responsibility. In the
        context of Islamic law, it refers to the act of one person taking
        responsibility for another’s obligation, whether financial or otherwise.
        This responsibility may involve repayment of a debt, delivery of goods,
        or fulfillment of an agreed duty in case the original obligor fails to
        do so.
        <br />
        The permissibility of Dhaman is grounded in the Quran, Sunnah, and
        consensus of the scholars (Ijma). The Quran calls for cooperation in
        righteous acts and the fulfillment of obligations. In Surah Al-Ma'idah,
        Allah commands believers to cooperate in goodness and righteousness, and
        not in sin and transgression. This principle of cooperation provides a
        moral basis for Dhaman, as it involves helping others to fulfill their
        duties in a manner that promotes justice and compassion. Likewise, Allah
        commands in the Qur'an, <br />
        “O believers! Honour your obligations”
        <a href="https://quran.com/5/1" target="blank">
          (Surah Al Ma'idah 1)
        </a>
        <br />
        This command includes contractual obligations and promises made to
        others. A guarantee falls within this broader command to honor
        commitments.
        <br />
        The Sunnah of the Prophet Muhammad further reinforces the validity and
        moral merit of Dhaman. One well-known incident illustrating this is
        narrated by Abu Qatadah, may Allah be pleased with him. When a deceased
        man was brought for the Prophet to lead his funeral prayer, he asked
        whether the man had any debts. Upon hearing that the man owed two
        dinars, the Prophet refused to lead the prayer until Abu Qatadah said,
        “I shall pay them, Messenger of Allah.”{" "}
        <a href="https://sunnah.com/abudawud:3343" target="blank">
          (Sunan Abi Dawood 3343)
        </a>
        <br />
        The Prophet then performed the prayer. This hadith, recorded in Sunan
        Abu Dawood, emphasizes that taking responsibility for another’s debt is
        a virtuous act that can relieve the deceased and their family of
        hardship. It also demonstrates how Dhaman strengthens social bonds and
        encourages moral accountability within the community.
        <br />
        Dhaman in Islamic jurisprudence may take two major forms: a permissible
        or voluntary guarantee and an obligatory or binding guarantee. The
        voluntary guarantee occurs when someone willingly undertakes to pay the
        debt or fulfill the obligation of another without being asked or
        compelled. This reflects the spirit of brotherhood in Islam, as
        believers are urged to support one another in times of need. The second
        form, the obligatory guarantee, arises when a person makes a clear and
        binding commitment, such as telling a merchant, “I will pay for whatever
        this person buys.” In such a case, once the transaction occurs based on
        that assurance, the guarantor becomes legally bound to fulfill the
        payment if the buyer defaults. This is not only a moral act but a legal
        responsibility recognized by Islamic law.
        <br />
        The underlying rationale for allowing Dhaman lies in the preservation of
        social stability and economic trust. In societies where financial
        transactions are common, the presence of a guarantor increases
        confidence between buyers, sellers, lenders, and borrowers. It reduces
        uncertainty (gharar) and promotes the Islamic objective of maintaining
        justice and balance in dealings. By ensuring that debts and promises are
        fulfilled, Dhaman aligns with the higher objectives of Shariah, known as
        Maqasid al-Shariah, particularly the protection of wealth (hifz al-mal)
        and the prevention of harm (daf’ al-darar).
        <br />
        <br />
        <b>Rulings, Conditions, and Ethics of Dhaman</b>
        <br />
        <br />
        The jurists of Islam have laid down detailed rulings regarding how
        Dhaman should be structured and implemented. They agree that a valid
        guarantee requires the consent of the guarantor and the creditor, while
        the consent of the debtor is not a necessary condition. This is because
        the guarantee is an additional security for the creditor and not a
        burden on the debtor. The guarantor’s consent, however, must be freely
        given, as coercion invalidates the act. Islam places great emphasis on
        mutual consent in contracts. The Prophet Muhammad said, “The sale is
        based on mutual consent.” This general principle extends to guarantees
        and other forms of financial responsibility.
        <br />
        The right of recourse is another essential feature of Dhaman. It means
        that once the guarantor pays off the debtor’s liability, they have the
        right to seek reimbursement from the debtor. This preserves fairness, as
        the guarantor is not meant to suffer loss due to their kindness. The
        creditor, however, can demand repayment from either the debtor or the
        guarantor. This flexibility ensures that debts are settled promptly,
        aligning with the divine directive to honor obligations.
        <br />
        In cases where the debtor dies, the debt remains a binding obligation.
        The Prophet Muhammad said, “The believer’s soul is suspended by his debt
        until it is settled for him.”
        <a href="https://sunnah.com/tirmidhi:1078" target="blank">
          (Jami AT-Tirmidhi 1078)
        </a>
        <br /> This hadith underscores the seriousness of debt in Islam and the
        importance of settling it promptly. If a guarantee exists, the creditor
        may choose to collect from the guarantor or the estate of the deceased
        debtor. This ruling ensures that financial justice is maintained even
        after death, reflecting Islam’s commitment to upholding the rights of
        others.
        <br />
        Dhaman is, by its nature, an act of voluntary cooperation and moral
        excellence. However, once undertaken, it becomes a binding
        responsibility. A person cannot retract a guarantee after it has been
        accepted by the creditor and relied upon in a transaction. The Shariah
        does not allow guarantees to be used as tools of deception or delay.
        Therefore, a guarantor must carefully consider their ability before
        committing to such an act.
        <br />
        From an ethical perspective, Dhaman reflects the principle of social
        solidarity. It helps the weak and needy to access credit or enter into
        business transactions that they otherwise could not. At the same time,
        it provides assurance to creditors that their rights will be protected.
        This mutual benefit is consistent with the Islamic economic philosophy
        of fairness and compassion.
        <br />
        Another important aspect is the voluntary nature of Dhaman. A person
        cannot be forced to guarantee someone else’s debt, as that would
        constitute unjust coercion. The Quran warns against consuming others’
        wealth unjustly and emphasizes that all transactions should be conducted
        with mutual consent. Therefore, the validity of Dhaman depends on the
        free will and intention of the guarantor.
        <br />
        Dhaman also promotes honesty and responsibility in financial dealings.
        When individuals know that others may have to bear the burden of their
        unpaid debts, it encourages them to fulfill their obligations
        diligently. This contributes to a more honest and reliable economic
        environment. In this way, Dhaman serves not just a legal purpose but a
        moral one, strengthening the fabric of trust in Muslim communities.
        <br />
        <br /> <b>The Concept and Legal Implications of Kafalah</b>
        <br />
        <br />
        Kafalah, or patronage, is another form of guarantee in Islamic law,
        though it differs from Dhaman in nature and application. Linguistically,
        Kafalah means to take care of, to protect, or to vouch for someone. In
        legal terms, it refers to a person (the Kafeel) taking responsibility
        for presenting another person (the Makfool) before a court, creditor, or
        authority when required. Unlike Dhaman, which primarily concerns
        financial liability, Kafalah deals with ensuring the presence or
        performance of an individual.
        <br />
        The legitimacy of Kafalah is derived from the Quran and Sunnah, which
        encourage believers to cooperate in righteousness and justice. The
        Quranic command,
        <br />
        “Cooperate with one another in goodness and righteousness, and do not
        cooperate in sin and transgression. And be mindful of Allah. Surely
        Allah is severe in punishment.”
        <a href="blank" target="_blank">
          (Surah Al Ma'idah 2)
        </a>{" "}
        <br />
        This verse serves as a moral foundation for Kafalah. It highlights the
        importance of mutual assistance and accountability.
        <br />A well-known hadith reported by al-Tirmidhi states: “ A loan must
        be paid back, a she-camel lent for a time for milking must be returned,
        a debt must be discharged, one who stands surety is held responsible.”{" "}
        <a href="https://sunnah.com/abudawud:3565" target="_blank">
          (Sunan Abi Dawood 1335)
        </a>
        <br />
        This concise statement of the Prophet Muhammad lays down three
        fundamental principles of Islamic financial ethics: returning borrowed
        property, fulfilling guarantees, and paying debts. Together, they form
        the ethical framework that governs Dhaman and Kafalah alike.
        <br />
        Kafalah serves several practical purposes in Islamic law. It can be used
        to ensure the presence of an accused person in a court of law, to
        guarantee that a worker completes their assigned duties, or to assure a
        creditor that a debtor will appear to settle their obligations. It plays
        a crucial role in maintaining social order and justice, as it prevents
        individuals from evading their responsibilities.
        <br />
        One of the key differences between Dhaman and Kafalah lies in the type
        of responsibility assumed. In Dhaman, the guarantor becomes responsible
        for the payment or fulfillment of an obligation if the principal fails
        to do so. In Kafalah, however, the Kafeel is only responsible for
        producing the Makfool when required. If the Kafeel successfully presents
        the person, they are absolved of further responsibility. The Kafeel does
        not bear financial liability for the debts or actions of the Makfool
        unless specifically agreed upon or required by local custom.
        <br />
        Another distinction is the severity of the obligation. Dhaman creates a
        direct financial liability that must be fulfilled, while Kafalah
        generally does not involve such liability unless the Kafeel fails to
        perform their duty of presenting the Makfool. Furthermore, if the
        Makfool dies, the Kafeel’s responsibility ends, as the purpose of the
        Kafalah i.e producing the person, can no longer be achieved. This
        differs from Dhaman, where the financial responsibility may persist even
        after the debtor’s death.
        <br />
        Scholars agree that Kafalah must be a voluntary act. The Kafeel must
        willingly accept the responsibility, and coercion invalidates the
        agreement. Interestingly, the consent of the Makfool is not necessary
        for the contract to be valid. This is because Kafalah primarily benefits
        the Makfool by providing an alternative to detention or restriction.
        However, the consent of the creditor or the party to whom the person is
        to be presented is necessary, as they are the primary beneficiary of the
        guarantee.
        <br />
        A practical example helps illustrate how Kafalah works. Suppose a man is
        accused of owing a debt but promises to repay it at a later date. The
        creditor may request that someone act as a Kafeel to ensure the man’s
        appearance on the agreed date. If the Kafeel succeeds in presenting the
        debtor, they are free from any further obligation. But if the debtor
        absconds and cannot be found, the Kafeel may be held responsible under
        certain local customs or agreements. This ensures that justice is
        maintained while providing flexibility and fairness to all parties
        involved.
        <br />
        <br />
        <b>Comparative Insights, Significance, and Modern Applications</b>
        <br />
        <br /> Both Dhaman and Kafalah embody the Islamic principles of mutual
        responsibility, social cooperation, and financial justice. They play
        vital roles in the smooth functioning of economic and legal systems,
        both in classical times and in modern societies. While their forms and
        details may differ, their shared objective is to protect the rights of
        individuals and to ensure that obligations are honored.
        <br />
        From a comparative perspective, Dhaman focuses on the guarantee of
        property, money, or performance of a contract, whereas Kafalah centers
        on guaranteeing the person himself. Dhaman often comes into play in
        commercial transactions, credit sales, or debts, while Kafalah is more
        relevant in judicial matters, employment contracts, or any situation
        requiring assurance of presence. Both are permissible as long as they
        are free from elements of injustice, exploitation, or usury.
        <br />
        The ethical significance of these contracts lies in their embodiment of
        Islamic values. They encourage Muslims to help one another in fulfilling
        obligations and to build a society based on trust and cooperation. By
        providing assurance to creditors and employers, these institutions
        reduce economic friction and promote moral responsibility. They also
        discourage deceit and negligence, as individuals are aware that others’
        trust and resources may be at stake due to their actions.
        <br />
        Dhaman and Kafalah together form a coherent framework of social and
        financial security in Islam. They reflect the faith’s deep concern for
        justice, cooperation, and the fulfillment of obligations. These
        mechanisms enable individuals and communities to interact economically
        and socially with confidence, knowing that their rights are protected by
        law and conscience alike. By understanding and applying these
        principles, Muslims can build systems of fairness that reflect the
        spirit of the Shariah, ensuring that compassion and justice remain at
        the heart of all dealings.
      </p>

      <table className="trade-table">
        <tr>
          <th>Dhaman</th>
          <th>Kafalah</th>
        </tr>
        <tr>
          <td>
            Taking responsibility for another person’s financial liability or
            debt.
          </td>
          <td>
            Taking responsibility for the person i.e ensuring their presence,
            performance, or fulfillment of an obligation.
          </td>
        </tr>
        <tr>
          <td>It relates to money, goods, or debts owed to another party.</td>
          <td>
            It relates to a person or their actions, such as appearing in court
            or completing a task.
          </td>
        </tr>
        <tr>
          <td>A person says, “If he does not pay the debt, I will pay it.”</td>
          <td>
            A person says, “I guarantee that he will appear before the judge.”
          </td>
        </tr>
        <tr>
          <td>
            Involves financial obligations (e.g., repayment of loans,
            compensation).
          </td>
          <td>
            Involves personal obligations (e.g., presence, delivery, or
            performance).
          </td>
        </tr>
        <tr>
          <td>The guarantor must pay if the debtor fails to pay.</td>
          <td>
            The guarantor must ensure the person appears or performs, otherwise
            may face consequences.
          </td>
        </tr>
      </table>
      <p className="content">
        <b>Coming up next</b>
        <br />
        <br />
        In tomorrow’s session, we will explore Al-Hawala, which is the Islamic
        concept of transference of debt. This principle allows a debtor to shift
        their financial obligation to a third party, streamlining repayment
        while maintaining justice and transparency. We'll study its conditions,
        benefits, and legal framework, and examine how the Prophet Muhammad
        (peace be upon him) endorsed this practice to ensure ease and trust in
        financial dealings. This lesson will deepen your understanding of how
        Islamic finance offers flexible, ethical solutions for managing debt.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

   <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage13;
